import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Text,
  Input,
  Select,
  useToast,
  VStack,
  FormControl,
  FormLabel,
  HStack,
  useColorModeValue,
  Checkbox,
  SimpleGrid,
} from '@chakra-ui/react';
import AdminDrawerContent from '../layout/AdminDrawerContent';
import {
  compactFormLabelProps,
  compactInputProps,
  compactSelectProps,
} from '../../theme/adminUi';
import { getData, postData } from '../../utils/httpClient';

const ClientsEditModal = ({
  isOpen,
  onClose,
  customer,
  isNew,
  onCreate,
  onUpdate,
}) => {
  const [formData, setFormData] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [customerTypesList, setCustomerTypesList] = useState([]);
  const [localityName, setLocalityName] = useState('');

  const toast = useToast();
  const inputColor = useColorModeValue('black', 'gray.400');
  const fieldProps = { ...compactInputProps, color: inputColor };
  const selectProps = { ...compactSelectProps, color: inputColor };

  const getDepartments = async (countryId = null) => {
    try {
      const url = countryId ? `/departments/country/${countryId}` : '/departments';
      const response = await getData(url);
      setDepartmentsList(response?.payload?.departments || []);
    } catch (error) {
      toast({
        title: 'Error al obtener departamentos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getCustomerTypes = async () => {
    try {
      const response = await getData('/customerTypes');
      setCustomerTypesList(response?.payload?.customerTypes || []);
    } catch (error) {
      toast({ title: 'Error al cargar tipos de cliente', status: 'error', duration: 3000 });
    }
  };

  const getCountries = async () => {
    try {
      const response = await getData('/countries');
      setCountryList(response?.payload?.countries || []);
    } catch (error) {
      toast({ title: 'Error al cargar países', status: 'error', duration: 3000 });
    }
  };

  useEffect(() => {
    getCountries();
    getCustomerTypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const uyId =
      customer?.countryId ||
      countryList.find((c) => c.name?.toUpperCase() === 'URUGUAY')?.id ||
      0;

    setFormData({
      id: customer?.id ?? null,
      name: customer?.name ?? '',
      document: customer?.document ?? '',
      address: customer?.address ?? '',
      customerTypeId: customer?.customerTypeId ?? 0,
      phone: customer?.phone ?? '',
      mobilePhone: customer?.mobilePhone ?? '',
      email: customer?.email ?? '',
      localityId: customer?.localityId ?? 0,
      departmentId: customer?.locality?.departmentId ?? 0,
      countryId: uyId,
      regularCustomer: customer?.regularCustomer ?? false,
    });
    setLocalityName(customer?.locality?.name ?? '');

    if (uyId) getDepartments(uyId);
    if (customer?.locality?.departmentId) {
      getDepartments(customer.countryId || uyId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, isNew, isOpen, countryList]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const formattedValue =
      type === 'checkbox'
        ? checked
        : ['customerTypeId', 'countryId', 'departmentId', 'localityId'].includes(name)
          ? value === ''
            ? 0
            : Number(value)
          : value;

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    if (name === 'countryId' && value !== '0') {
      getDepartments(value);
      setFormData((prev) => ({ ...prev, countryId: Number(value), departmentId: 0 }));
      setLocalityName('');
    }
    if (name === 'departmentId') {
      setLocalityName('');
    }
  };

  const resolveLocalityId = async () => {
    const name = localityName?.trim();
    if (!name) return null;
    if (!formData.departmentId) {
      return null;
    }
    try {
      const res = await getData(`/localities/department/${formData.departmentId}`);
      const list = res?.payload?.localities || [];
      const match = list.find(
        (l) => l.name?.toLowerCase() === name.toLowerCase(),
      );
      if (match) return match.id;
      const created = await postData('/localities', {
        name,
        departmentId: formData.departmentId,
      });
      return created?.id ?? created?.payload?.id ?? null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }

    const localityId = await resolveLocalityId();

    const customerData = {
      id: formData.id,
      name: formData.name.trim(),
      document: formData.document?.trim() || null,
      address: formData.address?.trim() || null,
      customerTypeId: formData.customerTypeId === 0 ? null : formData.customerTypeId,
      phone: formData.phone?.trim() || null,
      mobilePhone: formData.mobilePhone?.trim() || null,
      email: formData.email?.trim() || null,
      localityId,
      countryId: formData.countryId === 0 ? null : formData.countryId,
      regularCustomer: formData.regularCustomer,
    };

    if (isNew) {
      onCreate(customerData);
    } else {
      onUpdate(customerData);
    }
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={false}>
      <DrawerOverlay />
      <AdminDrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="bold">
          {isNew ? 'Nuevo cliente' : 'Editar cliente'}
        </DrawerHeader>
        <DrawerBody pb={6}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl isRequired>
                <FormLabel {...compactFormLabelProps}>Nombre</FormLabel>
                <Input name="name" value={formData.name || ''} onChange={handleChange} {...fieldProps} />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Documento</FormLabel>
                <Input
                  name="document"
                  value={formData.document || ''}
                  onChange={handleChange}
                  placeholder="CI / RUT (opcional)"
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Dirección</FormLabel>
                <Input
                  name="address"
                  value={formData.address || ''}
                  onChange={handleChange}
                  {...fieldProps}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl>
                <FormLabel {...compactFormLabelProps}>País</FormLabel>
                <Select
                  name="countryId"
                  value={formData.countryId || 0}
                  onChange={handleChange}
                  {...selectProps}
                >
                  <option value={0}>Seleccionar país</option>
                  {countryList.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Departamento</FormLabel>
                <Select
                  name="departmentId"
                  value={formData.departmentId || 0}
                  onChange={handleChange}
                  isDisabled={!formData.countryId}
                  {...selectProps}
                >
                  <option value={0}>Seleccionar departamento</option>
                  {departmentsList.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Localidad</FormLabel>
                <Input
                  value={localityName}
                  onChange={(e) => setLocalityName(e.target.value)}
                  placeholder="Escriba la localidad"
                  {...fieldProps}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl>
                <FormLabel {...compactFormLabelProps}>Teléfono</FormLabel>
                <Input name="phone" value={formData.phone || ''} onChange={handleChange} {...fieldProps} />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Celular</FormLabel>
                <Input
                  name="mobilePhone"
                  value={formData.mobilePhone || ''}
                  onChange={handleChange}
                  {...fieldProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Mail</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  {...fieldProps}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl display="flex" alignItems="flex-end" minH="56px">
                <Checkbox
                  name="regularCustomer"
                  isChecked={!!formData.regularCustomer}
                  onChange={handleChange}
                  colorScheme="gray"
                >
                  <Text fontSize="sm">Cliente habitual</Text>
                </Checkbox>
              </FormControl>

              <FormControl>
                <FormLabel {...compactFormLabelProps}>Tipo de cliente</FormLabel>
                <Select
                  name="customerTypeId"
                  value={formData.customerTypeId || 0}
                  onChange={handleChange}
                  {...selectProps}
                >
                  <option value={0}>Seleccionar tipo</option>
                  {customerTypesList.map((customerType) => (
                    <option key={customerType.id} value={customerType.id}>
                      {customerType.type}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
          </VStack>
        </DrawerBody>
        <HStack justify="flex-end" p={4} borderTopWidth="1px" borderColor="gray.100">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" variant="adminPrimary" onClick={handleSave}>
            Guardar
          </Button>
        </HStack>
      </AdminDrawerContent>
    </Drawer>
  );
};

export default ClientsEditModal;
